import { cn } from '@vidahealth/vida-ui';
import { BlurView, type BlurViewProps } from 'expo-blur';
import { cssInterop } from 'nativewind';
import { Platform, View, type ViewProps } from 'react-native';

cssInterop(BlurView, { className: 'style' });

type FrostedSurfaceProps = ViewProps & {
  intensity?: BlurViewProps['intensity'];
  tint?: BlurViewProps['tint'];
};

export function FrostedSurface({
  children,
  className,
  style,
  intensity = 60,
  tint = 'default',
  ...rest
}: FrostedSurfaceProps) {
  if (Platform.OS === 'web') {
    // Keep backdrop-filter on a sibling layer, not the content wrapper.
    // On Android Chrome, filter/backdrop-filter on the same element as
    // scroll-adjacent children can promote separate compositor layers so the
    // CTA appears to slide independently during vertical scroll.
    //
    // NOTE: the blur layer below is `position: absolute; inset: 0`, so the outer
    // View must be a positioning context — callers pass `absolute`/`relative` via
    // className (all current consumers do). Without it the fill anchors to the
    // nearest positioned ancestor and the frost renders at the wrong size.
    return (
      <View className={cn(className)} style={style} {...rest}>
        <View
          aria-hidden
          className='bg-background-0/70'
          style={[
            {
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              pointerEvents: 'none',
            } as object,
          ]}
        />
        {children}
      </View>
    );
  }

  return (
    <BlurView
      intensity={intensity}
      tint={tint}
      experimentalBlurMethod='dimezisBlurView'
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </BlurView>
  );
}
